import { parseCsv } from './parseCsv';

export const ARMS = [
  { id: 'Cervical', label: 'Cervical' },
  { id: 'Thoracolumbar', label: 'Thoracolumbar' },
  { id: 'Adult Spinal Deformity', label: 'Adult Spinal Deformity' },
];

export const REGIONS = [
  { id: 'Cervical', label: 'Cervical region' },
  { id: 'Thoracolumbar', label: 'Thoracolumbar region' },
];

export const FOLLOW_UP_VISITS = [
  { id: 'initial', label: 'Initial follow-up', window: '2–8 weeks', fromDay: 14, throughDay: 56 },
  { id: 'month-12', label: '12-month', window: '± 3 months', fromDay: 274, throughDay: 456 },
  { id: 'month-24', label: '24-month', window: '± 4 months', fromDay: 608, throughDay: 852 },
  { id: 'unscheduled', label: 'Unscheduled', window: 'As needed' },
];

const GENERIC_TOKENS = new Set([
  'system', 'systems', 'spinal', 'the', 'and', 'with', 'for', 'device', 'devices',
  'of', 'a', 'plate', 'plates', 'screw', 'screws',
]);

const SHORT_TOKENS = new Set(['al', 'ti', 'mi', 'xl', 'xt']);

export function loadCompassFields(csvText) {
  return parseCsv(csvText).filter((field) => field.protocol === 'COMPASS' && field.field_key);
}

export function fieldAppliesToCase(field, arm, region) {
  const arms = field.sub_protocol.split(';').map((part) => part.trim()).filter(Boolean);
  if (!arms.includes('All') && !arms.includes(arm)) return false;
  if (field.asd_region === 'Cervical or Thoracolumbar') {
    return arm === 'Adult Spinal Deformity';
  }
  if (field.asd_region === 'Cervical' || field.asd_region === 'Thoracolumbar') {
    if (arm === 'Adult Spinal Deformity') return region === field.asd_region;
    if (arm === 'Cervical') return field.asd_region === 'Cervical';
    if (arm === 'Thoracolumbar') return field.asd_region === 'Thoracolumbar';
    return false;
  }
  return true;
}

function historyCode(arm) {
  if (arm === 'Cervical') return '08-A';
  if (arm === 'Thoracolumbar') return '08-B';
  return '08-C';
}

function enrollCode(arm) {
  if (arm === 'Cervical') return '02-A';
  if (arm === 'Thoracolumbar') return '02-B';
  return '02-C';
}

function postopCode(arm) {
  if (arm === 'Cervical') return '03-A';
  if (arm === 'Thoracolumbar') return '03-B';
  return '03-C';
}

function operativeCode(arm) {
  if (arm === 'Cervical') return '11-A';
  if (arm === 'Thoracolumbar') return '11-B';
  return '11-C';
}

function hiddenOnForms(field) {
  if (field.form_code === 'FOOTER' || field.form_code === 'VISIT' || field.form_code === 'IE') return true;
  if (/^enroll\.[^.]+\.sub_protocol$/.test(field.field_key)) return true;
  if (field.field_key === 'enroll.asd.region') return true;
  return false;
}

export function splitCaseFields(allFields, arm, region) {
  const applicable = allFields.filter((field) => fieldAppliesToCase(field, arm, region));
  return {
    fields: applicable.filter((field) => field.field_kind !== 'eligibility_criterion' && !hiddenOnForms(field)),
    criteria: applicable.filter((field) => field.field_kind === 'eligibility_criterion'),
    reviewers: applicable.filter((field) => field.form_code === 'IE' && field.field_kind === 'capture'),
    visitDate: applicable.find((field) => field.field_key === 'visit_date') || null,
    unscheduledReason: applicable.find((field) => field.field_key === 'unscheduled_visit_reason') || null,
  };
}

function formEntry(id, title, fields) {
  if (!fields.length) return null;
  return { id, title, fields };
}

export function buildOutline(formFields, arm) {
  const take = (predicate) => formFields.filter(predicate);
  const enrollment = [
    formEntry('subject', 'Subject', take((field) => field.form_code === 'ID')),
    formEntry('consent', 'Informed consent', take((field) => field.form_code === '01')),
    formEntry('enroll', 'Eligibility and devices', take((field) => (
      field.form_code === enrollCode(arm) || field.field_key.startsWith('device.')
    ))),
    formEntry('criteria', 'Inclusion and exclusion', [{ field_key: 'criteria-placeholder', field_label: 'Criteria' }]),
  ].filter(Boolean);

  const preop = [
    formEntry('demographics', 'Demographics', take((field) => field.form_code === '04')),
    formEntry('exam', 'Physical exam', take((field) => field.form_code === '05')),
    formEntry('tobacco', 'Tobacco', take((field) => field.form_code === '06')),
    formEntry('compensation', 'Work and litigation', take((field) => field.form_code === '07')),
    formEntry('history', 'Spine history', take((field) => field.form_code === historyCode(arm))),
    formEntry('symptoms', 'Symptoms and neuro exam', take((field) => (
      field.form_code === '09/16-cervical' || field.form_code === '09/16-thoracolumbar'
    ))),
    formEntry('xray', 'X-rays', take((field) => field.form_code === '10')),
    formEntry('proms', 'Questionnaires', take((field) => field.form_code === 'PROM')),
  ].filter(Boolean);

  const surgery = [
    formEntry('postop', 'Post-op eligibility', take((field) => field.form_code === postopCode(arm))),
    formEntry('operative', 'Operative form', take((field) => (
      (field.form_code === '11' || field.form_code === operativeCode(arm))
      && !field.field_key.startsWith('device.')
    ))),
    formEntry('catalog', 'Device catalog', take((field) => field.form_code === '12')),
    formEntry('procedure', 'Procedure details', take((field) => field.form_code === '13')),
    formEntry('graft', 'Grafting', take((field) => field.form_code === '14')),
  ].filter(Boolean);

  const followForms = [
    formEntry('follow-exam', 'Clinical exam', take((field) => field.form_code === '15')),
    formEntry('follow-symptoms', 'Symptoms and neuro exam', take((field) => (
      field.form_code === '09/16-cervical' || field.form_code === '09/16-thoracolumbar'
    ))),
    formEntry('follow-xray', 'X-rays', take((field) => field.form_code === '16')),
    formEntry('follow-proms', 'Questionnaires', take((field) => field.form_code === 'PROM')),
  ].filter(Boolean);

  const logs = [
    formEntry('meds', 'Medications', take((field) => field.form_code === 'CM-01')),
    formEntry('events', 'Adverse events', take((field) => field.form_code === 'AE-03')),
    formEntry('deviations', 'Protocol deviations', take((field) => field.form_code === 'PD-02')),
    formEntry('surgeries', 'Later surgeries', take((field) => field.form_code === 'SSI-06')),
    formEntry('procedures', 'Other procedures', take((field) => field.form_code === 'CP-07')),
    formEntry('reconsent', 'Re-consent', take((field) => field.form_code === 'RC-05')),
  ].filter(Boolean);

  const exit = [
    formEntry('exit', 'Study exit', take((field) => field.form_code === 'EX-04')),
  ].filter(Boolean);

  return [
    { id: 'enrollment', label: 'Enrollment', window: 'Before surgery', forms: enrollment },
    { id: 'preop', label: 'Pre-op', window: 'Within 30 days of surgery', visitId: 'pre-op', forms: preop },
    { id: 'surgery', label: 'Surgery', window: 'Day of surgery through discharge', forms: surgery },
    {
      id: 'followup',
      label: 'Follow-up',
      visits: FOLLOW_UP_VISITS.map((visit) => ({ ...visit, forms: followForms })),
    },
    { id: 'logs', label: 'Logs', window: 'Any time in the study', forms: logs },
    { id: 'exit', label: 'Exit', window: '24-month visit or earlier stop', forms: exit },
  ];
}

export function outlineStops(outline) {
  const stops = [];
  (outline || []).forEach((phase) => {
    if (phase.visits) {
      phase.visits.forEach((visit) => {
        (visit.forms || []).forEach((form) => {
          stops.push({ phase, visit, form, visitId: visit.id });
        });
      });
      return;
    }
    (phase.forms || []).forEach((form) => {
      stops.push({ phase, visit: null, form, visitId: phase.visitId || '' });
    });
  });
  return stops;
}

function visibleEntries(fieldList, values, valueKey) {
  const keyed = fieldList
    .map((field) => ({ ...field, field_key: valueKey(field) }))
    .filter((field) => isFieldVisible(field, values) && !isDerivedBmi(field));
  return bundleMeasuredValues(bundleGraftLocations(keyed)).map((item) => {
    if (item.kind === 'graft-location') {
      return { fieldKey: item.field_key, missing: graftLocationMissing(item, (field) => values[field.field_key]) };
    }
    if (item.kind === 'measure') {
      const number = values[item.field.field_key];
      const unit = values[item.unit.field_key];
      return { fieldKey: item.field.field_key, missing: !isFilledValue(number) || !isFilledValue(unit) };
    }
    return { fieldKey: item.field_key, missing: !isFilledValue(values[item.field_key]) };
  });
}

export function isGraftLocationField(field) {
  return /\.graft_(cage|facets|posterolateral|interbody|anterior|posterior|other)(#\d+)?$/.test(String(field?.field_key || ''));
}

export function isGraftSpecifyField(field) {
  return /\.graft_other_specify(#\d+)?$/.test(String(field?.field_key || ''));
}

export function isMeasureUnit(field) {
  return field?.data_type === 'single_select' && /(?:_|\.)unit(?:#\d+)?$/.test(String(field?.field_key || ''));
}

export function measureKeyForUnit(fieldKey) {
  return String(fieldKey || '').replace(/(?:_|\.)unit(?=#\d+$|$)/, '');
}

export function isDerivedBmi(field) {
  return String(field?.field_key || '').replace(/#\d+$/, '') === 'alliance.baseline.bmi';
}

/** A number and the unit selector beside it are one question. */
export function bundleMeasuredValues(fields) {
  const bundled = [];
  const list = fields || [];
  for (let index = 0; index < list.length; index += 1) {
    const field = list[index];
    const unit = list[index + 1];
    const numeric = ['integer', 'decimal', 'number'].includes(field?.data_type);
    const options = unit && !unit.kind ? splitOptions(unit.response_options) : [];
    if (
      numeric
      && !field.kind
      && unit
      && !unit.kind
      && isMeasureUnit(unit)
      && measureKeyForUnit(unit.field_key) === field.field_key
      && options.length
    ) {
      bundled.push({ kind: 'measure', field_key: field.field_key, field, unit });
      index += 1;
      continue;
    }
    bundled.push(field);
  }
  return bundled;
}

export function graftOptionLabel(field) {
  const label = String(field?.field_label || '').replace(/^Location of bone graft\s*-\s*/i, '').trim();
  if (!label) return 'Other';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Consecutive bone-graft location checkboxes, plus the Other specify line, are one question. */
export function bundleGraftLocations(fields) {
  const bundled = [];
  const list = fields || [];
  let index = 0;
  while (index < list.length) {
    if (!isGraftLocationField(list[index])) {
      bundled.push(list[index]);
      index += 1;
      continue;
    }
    const options = [];
    while (index < list.length && isGraftLocationField(list[index])) {
      options.push(list[index]);
      index += 1;
    }
    let specify = null;
    if (index < list.length && isGraftSpecifyField(list[index])) {
      specify = list[index];
      index += 1;
    }
    bundled.push({
      kind: 'graft-location',
      field_key: options[0].field_key,
      options,
      specify,
    });
  }
  return bundled;
}

function graftLocationMissing(group, valueFor) {
  const any = group.options.some((field) => valueFor(field) === true);
  const other = group.options.find((field) => /\.graft_other(#\d+)?$/.test(field.field_key));
  const otherOn = Boolean(other && valueFor(other) === true);
  const specifyOk = !group.specify || !otherOn || isFilledValue(valueFor(group.specify));
  return !any || !specifyOk;
}

/** Visible questions in the order they render, including ones that already have an answer. */
export function visibleFieldEntries(fields, values = {}, repeatState = {}) {
  const list = (fields || []).filter((field) => field.field_key && field.field_key !== 'criteria-placeholder');
  const { blocks, repeats } = buildBlocks(list);
  const entries = [];
  blocks.forEach((block) => {
    if (block.type === 'repeat') {
      const group = repeats.get(block.id);
      if (!group) return;
      entries.push(...visibleEntries(group.headers, values, (field) => field.field_key));
      const rows = repeatRowCount(group, values, repeatState);
      for (let index = 0; index < rows; index += 1) {
        entries.push(...visibleEntries(group.items, values, (field) => `${field.field_key}#${index}`));
      }
      return;
    }
    entries.push(...visibleEntries(block.fields, values, (field) => field.field_key));
  });
  return entries;
}

/** The next unanswered question after the cursor, then back to the first one. */
export function nextMissingTarget(forms, cursor = null) {
  const flat = [];
  (forms || []).forEach((item, formIndex) => {
    (item.fields || []).forEach((field, fieldIndex) => {
      flat.push({
        formKey: item.formKey,
        fieldKey: field.fieldKey,
        missing: Boolean(field.missing),
        formIndex,
        fieldIndex,
      });
    });
  });
  const missing = flat.filter((item) => item.missing);
  if (!missing.length) return null;
  const formKey = cursor?.formKey || '';
  const fieldKey = cursor?.fieldKey || '';
  if (fieldKey) {
    const cursorPos = flat.find((item) => item.formKey === formKey && item.fieldKey === fieldKey);
    if (cursorPos) {
      const after = missing.find((item) => (
        item.formIndex > cursorPos.formIndex
        || (item.formIndex === cursorPos.formIndex && item.fieldIndex > cursorPos.fieldIndex)
      ));
      if (after) return { formKey: after.formKey, fieldKey: after.fieldKey };
    }
  } else if (formKey) {
    const onForm = missing.find((item) => item.formKey === formKey);
    if (onForm) return { formKey: onForm.formKey, fieldKey: onForm.fieldKey };
    const formIndex = flat.find((item) => item.formKey === formKey)?.formIndex ?? -1;
    const after = missing.find((item) => item.formIndex > formIndex);
    if (after) return { formKey: after.formKey, fieldKey: after.fieldKey };
  }
  return { formKey: missing[0].formKey, fieldKey: missing[0].fieldKey };
}

export function firstLocation(outline) {
  for (const phase of outline) {
    if (phase.visits) {
      const visit = phase.visits.find((item) => item.forms.length);
      if (visit) return { phaseId: phase.id, visitId: visit.id, formId: visit.forms[0].id };
    } else if (phase.forms.length) {
      return { phaseId: phase.id, visitId: phase.visitId || '', formId: phase.forms[0].id };
    }
  }
  return { phaseId: 'enrollment', visitId: '', formId: 'subject' };
}

export function findLocation(outline, active) {
  if (!active) return null;
  for (const phase of outline) {
    if (phase.id !== active.phaseId) continue;
    if (phase.visits) {
      const visit = phase.visits.find((item) => item.id === active.visitId);
      const form = visit?.forms.find((item) => item.id === active.formId);
      if (form) return { phase, visit, form };
    } else {
      const form = phase.forms.find((item) => item.id === active.formId);
      if (form) return { phase, visit: null, form };
    }
  }
  return null;
}

export function instanceKey(formId, visitId) {
  return `${formId}::${visitId || 'case'}`;
}

function answerValue(answers, keyPart) {
  const found = Object.entries(answers).find(([key]) => key === keyPart || key.endsWith(`.${keyPart}`) || key.includes(keyPart));
  return found ? found[1] : undefined;
}

function optionSelected(value, option) {
  const items = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
  return items.some((item) => String(item).toLowerCase() === option.toLowerCase());
}

const VISIT_TRIGGERS = new Set([
  'alliance.visit.occurred',
  'alliance.visit.return_to_work',
  'alliance.visit.stable',
  'alliance.visit.stable.additional_surgery',
  'alliance.visit.stable.ae',
  'alliance.visit.therapies_any',
  'alliance.visit.therapies',
  'alliance.visit.crp.collected',
  'alliance.visit.hba1c.collected',
  'alliance.visit.nicotine.collected',
  'alliance.discharge.therapies_any',
  'alliance.discharge.therapies',
]);

function bareFieldKey(field) {
  return String(field?.field_key || field || '').replace(/#\d+$/, '');
}

/** Visit, work, stability, therapy, and lab lines stay hidden until the earlier answer applies. */
export function visitDetailOpen(field, answers) {
  const key = bareFieldKey(field);
  const values = answers || {};
  if (key === 'alliance.discharge.therapies' || key === 'alliance.discharge.therapies_other') {
    if (values['alliance.discharge.therapies_any'] !== 'Yes') return false;
    if (key === 'alliance.discharge.therapies_other') return optionSelected(values['alliance.discharge.therapies'], 'Other');
    return true;
  }
  if (!key.startsWith('alliance.visit.') || key === 'alliance.visit.occurred') return null;
  if (values['alliance.visit.occurred'] !== 'Yes') return false;
  if (key === 'alliance.visit.return_to_work_date') return values['alliance.visit.return_to_work'] === 'Yes';
  if (key.startsWith('alliance.visit.stable.')) {
    if (values['alliance.visit.stable'] !== 'No') return false;
    if (key === 'alliance.visit.stable.which_surgery') return values['alliance.visit.stable.additional_surgery'] === 'Yes';
    if (key === 'alliance.visit.stable.which_ae') return values['alliance.visit.stable.ae'] === 'Yes';
    return true;
  }
  if (key === 'alliance.visit.therapies' || key === 'alliance.visit.therapies_other') {
    if (values['alliance.visit.therapies_any'] !== 'Yes') return false;
    if (key === 'alliance.visit.therapies_other') return optionSelected(values['alliance.visit.therapies'], 'Other');
    return true;
  }
  if (key === 'alliance.visit.crp.level' || key === 'alliance.visit.crp.date') return values['alliance.visit.crp.collected'] === 'Yes';
  if (key === 'alliance.visit.hba1c.level' || key === 'alliance.visit.hba1c.date') return values['alliance.visit.hba1c.collected'] === 'Yes';
  if (key === 'alliance.visit.nicotine.level' || key === 'alliance.visit.nicotine.date') return values['alliance.visit.nicotine.collected'] === 'Yes';
  return true;
}

const BASELINE_TEST_GATES = {
  'alliance.baseline.dexa': 'completed',
  'alliance.baseline.vitamin_d': 'collected',
  'alliance.baseline.crp': 'collected',
  'alliance.baseline.hba1c': 'collected',
  'alliance.baseline.nicotine': 'collected',
  'alliance.baseline.neuro': 'completed',
  'alliance.baseline.vas': 'completed',
  'alliance.baseline.odi': 'completed',
  'alliance.baseline.ndi': 'completed',
  'alliance.baseline.eq5d': 'completed',
  'alliance.baseline.promis10': 'completed',
  'alliance.baseline.promis_pi': 'completed',
  'alliance.baseline.srs': 'completed',
};

/** The completed or collected question that controls a baseline test's later lines. */
export function baselineTestGate(field) {
  const key = bareFieldKey(field);
  if (!key.startsWith('alliance.baseline.')) return null;
  const parts = key.split('.');
  if (parts.length < 4) return null;
  const stem = parts.slice(0, 3).join('.');
  const kind = BASELINE_TEST_GATES[stem];
  if (!kind) return null;
  const leaf = parts.slice(3).join('.');
  if (!leaf || leaf === 'completed' || leaf === 'collected' || leaf === 'status') return null;
  return { gateKey: `${stem}.${kind}` };
}

/** Baseline test lines stay hidden until that test was completed or collected. */
export function baselineDetailOpen(field, answers) {
  const gate = baselineTestGate(field);
  if (!gate) return null;
  if ((answers || {})[gate.gateKey] !== 'Yes') return false;
  const when = field?.applies_when || '';
  if (/\bis Other\b/i.test(when) || /\bOther is selected\b/i.test(when)) return null;
  return true;
}

/** True once a baseline test was marked not completed, so its later lines can be stored as N/A. */
export function baselineNaReady(field, answers) {
  const gate = baselineTestGate(field);
  if (!gate) return false;
  const answer = (answers || {})[gate.gateKey];
  return answer != null && answer !== '' && answer !== 'Yes';
}

export function isBaselineTestTrigger(fieldKey) {
  const key = bareFieldKey(fieldKey);
  return Object.entries(BASELINE_TEST_GATES).some(([stem, kind]) => key === `${stem}.${kind}`);
}

const INDICATION_SECTIONS = [
  ['alliance.indication.deformity.', 'Deformity'],
  ['alliance.indication.degen.', 'Degenerative disease'],
  ['alliance.indication.revision.', 'Fusion failure/revision surgery'],
  ['alliance.indication.tumor.', 'Tumor case'],
  ['alliance.indication.fracture_traumatic.', 'Fracture - traumatic'],
  ['alliance.indication.fracture_path.', 'Fracture - pathological'],
  ['alliance.indication.spondy.', 'Spondylolisthesis (non-degenerative)'],
  ['alliance.indication.inflammation.', 'Inflammation/infection'],
];

const AE_HOSPITAL_DATES = new Set(['alliance.ae.hospital_start', 'alliance.ae.hospital_end']);
const AE_TREATMENT_FOLLOW_UPS = new Set([
  'alliance.ae.treatment',
  'alliance.ae.denervation_date',
  'alliance.ae.denervation_type',
  'alliance.ae.opiates',
  'alliance.ae.spinal_surgery',
  'alliance.ae.nonspinal',
  'alliance.ae.treatment_other',
]);

/** Prior spine surgery rows stay hidden until previous surgery is Yes. */
export function spineHistoryOpen(field, answers) {
  const key = bareFieldKey(field);
  if (!key.startsWith('alliance.spine_history.') || key === 'alliance.spine_history.any') return null;
  if (answers?.['alliance.spine_history.any'] !== 'Yes') return false;
  return null;
}

/** The prior-therapy write-in stays hidden until Other is selected. */
export function historyTherapyOpen(field, answers) {
  const key = bareFieldKey(field);
  if (key !== 'alliance.history.therapies_other') return null;
  return optionSelected(answers?.['alliance.history.therapies'], 'Other');
}

/** Hospital dates and treatment lines stay hidden until that adverse-event answer applies. */
export function adverseEventDetailOpen(field, answers) {
  const key = bareFieldKey(field);
  const values = answers || {};
  if (AE_HOSPITAL_DATES.has(key)) {
    const gate = values['alliance.ae.hospitalization'];
    return gate === 'New hospitalization' || gate === 'Prolongation of existing hospitalization';
  }
  if (!AE_TREATMENT_FOLLOW_UPS.has(key)) return null;
  if (values['alliance.ae.treatment_any'] !== 'Yes') return false;
  if (key === 'alliance.ae.treatment') return true;
  return null;
}

/** True once the adverse-event answer that hides a line has been chosen. */
export function adverseEventNaReady(field, answers) {
  const key = bareFieldKey(field);
  const values = answers || {};
  if (AE_HOSPITAL_DATES.has(key)) return Boolean(values['alliance.ae.hospitalization']);
  if (!AE_TREATMENT_FOLLOW_UPS.has(key)) return false;
  if (!values['alliance.ae.treatment_any']) return false;
  if (values['alliance.ae.treatment_any'] !== 'Yes') return true;
  if (key !== 'alliance.ae.treatment_other') return false;
  const types = values['alliance.ae.treatment'];
  return Array.isArray(types) ? types.length > 0 : Boolean(types);
}

/** Indication sections stay hidden until that answer is selected on the primary indication. */
export function indicationSectionOpen(field, answers) {
  const key = bareFieldKey(field);
  if (!key.startsWith('alliance.indication.') || key === 'alliance.indication.primary') return null;
  const section = INDICATION_SECTIONS.find(([prefix]) => key.startsWith(prefix));
  const primary = answers?.['alliance.indication.primary'];
  if (section) return optionSelected(primary, section[1]) ? null : false;
  const match = String(field.applies_when || '').match(/primary indication is\s+([^.;]+)/i);
  if (!match) return null;
  return optionSelected(primary, match[1].trim());
}

export function isFieldVisible(field, answers) {
  const key = String(field?.field_key || '').replace(/#\d+$/, '');
  if (key === 'alliance.baseline.age_90_or_older') return false;
  const spineHistory = spineHistoryOpen(field, answers);
  if (spineHistory !== null) return spineHistory;
  const historyTherapy = historyTherapyOpen(field, answers);
  if (historyTherapy !== null) return historyTherapy;
  const adverseEvent = adverseEventDetailOpen(field, answers);
  if (adverseEvent !== null) return adverseEvent;
  const when = field.applies_when || '';
  if (/sex is Female/i.test(when)) return answers.sex === 'Female';
  if (/race includes Other/i.test(when)) {
    return Array.isArray(answers.race) && answers.race.includes('Other');
  }
  if (/did not meet all eligibility/i.test(when)) {
    const met = answerValue(answers, 'met_all_eligibility');
    return met === 'No';
  }
  if (/all eligibility criteria were not met/i.test(when)) {
    return answerValue(answers, 'all_met') === 'No';
  }
  if (/not fully retrospective/i.test(when) || /consent was not obtained/i.test(when)) {
    if (/not fully retrospective/i.test(when)) return answers.fully_retrospective === 'No';
  }
  if (/consent was not obtained/i.test(when)) return answers.consent_obtained === 'No';
  if (/consent was obtained/i.test(when)) return answers.consent_obtained === 'Yes';
  if (/subject is fully retrospective/i.test(when)) return answers.fully_retrospective === 'Yes';
  if (/motor strength overall is Abnormal/i.test(when)) {
    return Object.entries(answers).some(([key, value]) => key.includes('motor_overall') && String(value).startsWith('Abnormal'));
  }
  if (/clinically relevant is Yes/i.test(when)) {
    const gate = field.field_key
      .replace(/_description$/, '_clinically_relevant')
      .replace(/_why_not_excluded$/, '_clinically_relevant');
    return answers[gate] === 'Yes';
  }
  if (/this diagnosis is Yes/i.test(when)) {
    const gate = field.field_key.replace(/_level_(from|to)$/, '');
    return answers[gate] === 'Yes';
  }
  if (/^If former smoker/i.test(when)) {
    return String(answers.tobacco_history || '').startsWith('Former');
  }
  if (/previous treatment is Yes/i.test(when)) {
    const gate = Object.keys(answers).find((key) => key.includes('.previous_treatment'));
    return gate ? answers[gate] === 'Yes' : false;
  }
  if (/^If Other was used/i.test(when)) {
    const gate = Object.keys(answers).find((key) => key.endsWith('.other') && key.includes('tx.'));
    return gate ? answers[gate] === true : false;
  }
  const yesFollowUp = indexYesFollowUpOpen(field, answers);
  if (yesFollowUp !== null) return yesFollowUp;
  const indicationSection = indicationSectionOpen(field, answers);
  if (indicationSection !== null) return indicationSection;
  const visitDetail = visitDetailOpen(field, answers);
  if (visitDetail !== null) return visitDetail;
  const baselineDetail = baselineDetailOpen(field, answers);
  if (baselineDetail !== null) return baselineDetail;
  if (/\bis Other\b/i.test(when) || /\bincludes? Other\b/i.test(when) || /\bOther is selected\b/i.test(when) || /non-Medtronic robot/i.test(when)) {
    return otherDetailVisible(field, answers);
  }
  const deviceFollowUp = deviceUsedVisible(field, answers);
  if (deviceFollowUp !== null) return deviceFollowUp;
  const procedureFollowUp = procedureFollowUpVisible(field, answers);
  if (procedureFollowUp !== null) return procedureFollowUp;
  const imagingFollowUp = imagingFollowUpVisible(field, answers);
  if (imagingFollowUp !== null) return imagingFollowUp;
  const graftSpecify = graftSpecifyVisible(field, answers);
  if (graftSpecify !== null) return graftSpecify;
  const deviceOther = deviceOtherWriteInVisible(field, answers);
  if (deviceOther !== null) return deviceOther;
  return true;
}

/** Device type and manufacturer write-ins stay hidden until that row's answer is Other. */
function deviceOtherWriteInVisible(field, answers) {
  const match = String(field?.field_key || '').match(/^(alliance\.device\.(?:device_type|manufacturer))_other(#\d+)?$/);
  if (!match) return null;
  return /^other\b/i.test(String(answers?.[`${match[1]}${match[2] || ''}`] || '').trim());
}

function graftSpecifyVisible(field, answers) {
  const match = String(field.field_key || '').match(/^(.*)\.graft_other_specify(#\d+)?$/);
  if (!match) return null;
  return answers[`${match[1]}.graft_other${match[2] || ''}`] === true;
}

/** Yes/No groups whose later questions stay hidden until the gate answer is Yes. */
export const INDEX_YES_GROUPS = [
  {
    gate: 'alliance.index.unid.used',
    keys: ['alliance.index.unid.plan', 'alliance.index.unid.bent_or_cut'],
  },
  {
    gate: 'alliance.index.robot.used',
    keys: [
      'alliance.index.robot.which',
      'alliance.index.robot.which_other',
      'alliance.index.robot.screws_planned',
      'alliance.index.robot.screws_placed',
      'alliance.index.robot.screws_difference',
      'alliance.index.robot.difference_reason',
      'alliance.index.robot.interbodies_navigated',
      'alliance.index.robot.malfunction',
    ],
  },
  {
    gate: 'alliance.index.nav.used',
    keys: [
      'alliance.index.nav.screw_validation',
      'alliance.index.nav.screws_planned',
      'alliance.index.nav.screws_placed',
      'alliance.index.nav.screws_difference',
      'alliance.index.nav.difference_reason',
      'alliance.index.nav.malfunction',
    ],
  },
  {
    gate: 'alliance.index.graft.used',
    keys: ['alliance.index.graft.types', 'alliance.index.graft.other'],
  },
  {
    gate: 'alliance.index.neuro.used',
    keys: [
      'alliance.index.neuro.reposition',
      'alliance.index.neuro.reposition_reason',
      'alliance.index.neuro.ae',
    ],
  },
];

/** Follow-ups in a Yes/No group stay hidden until that answer is Yes. */
export function indexYesFollowUpOpen(field, answers) {
  const key = String(field?.field_key || field || '').replace(/#\d+$/, '');
  const group = INDEX_YES_GROUPS.find((item) => item.keys.includes(key));
  if (!group) return null;
  if (answers?.[group.gate] !== 'Yes') return false;
  if (key === 'alliance.index.robot.which_other') {
    return /^other\b/i.test(String(answers['alliance.index.robot.which'] || ''));
  }
  if (key === 'alliance.index.graft.other') {
    return optionSelected(answers['alliance.index.graft.types'], 'Other');
  }
  return true;
}

function imagingChoice(field) {
  const key = String(field.field_key || '').replace(/#\d+$/, '');
  const when = field.applies_when || '';
  if (key.includes('.fluoro_') || /fluoroscopy was used/i.test(when)) return 'Fluoroscopy';
  if (key.includes('.oarm.') || /O-arm was used/i.test(when)) return 'O-arm';
  if (key.includes('.other_3d.') || /other 3D intra-operative imaging was used/i.test(when)) return 'Other 3D intra-operative imaging';
  return null;
}

/** True once the answer that controls a hidden visit line has been chosen. */
export function visitNaReady(field, answers) {
  const key = bareFieldKey(field);
  const values = answers || {};
  if (key === 'alliance.discharge.therapies' || key === 'alliance.discharge.therapies_other') {
    if (!values['alliance.discharge.therapies_any']) return false;
    if (key === 'alliance.discharge.therapies_other' && values['alliance.discharge.therapies_any'] === 'Yes') {
      const types = values['alliance.discharge.therapies'];
      return Array.isArray(types) ? types.length > 0 : Boolean(types);
    }
    return true;
  }
  if (!key.startsWith('alliance.visit.') || key === 'alliance.visit.occurred') return false;
  if (values['alliance.visit.occurred'] === 'No') return true;
  if (values['alliance.visit.occurred'] !== 'Yes') return false;
  if (key === 'alliance.visit.return_to_work_date') return Boolean(values['alliance.visit.return_to_work']);
  if (key.startsWith('alliance.visit.stable.')) {
    if (!values['alliance.visit.stable']) return false;
    if (key === 'alliance.visit.stable.which_surgery') {
      return values['alliance.visit.stable'] !== 'No' || Boolean(values['alliance.visit.stable.additional_surgery']);
    }
    if (key === 'alliance.visit.stable.which_ae') {
      return values['alliance.visit.stable'] !== 'No' || Boolean(values['alliance.visit.stable.ae']);
    }
    return true;
  }
  if (key === 'alliance.visit.therapies' || key === 'alliance.visit.therapies_other') {
    if (!values['alliance.visit.therapies_any']) return false;
    if (key === 'alliance.visit.therapies_other' && values['alliance.visit.therapies_any'] === 'Yes') {
      const types = values['alliance.visit.therapies'];
      return Array.isArray(types) ? types.length > 0 : Boolean(types);
    }
    return true;
  }
  if (key === 'alliance.visit.crp.level' || key === 'alliance.visit.crp.date') return Boolean(values['alliance.visit.crp.collected']);
  if (key === 'alliance.visit.hba1c.level' || key === 'alliance.visit.hba1c.date') return Boolean(values['alliance.visit.hba1c.collected']);
  if (key === 'alliance.visit.nicotine.level' || key === 'alliance.visit.nicotine.date') return Boolean(values['alliance.visit.nicotine.collected']);
  return false;
}

export function isVisitFollowUpTrigger(fieldKey) {
  return VISIT_TRIGGERS.has(bareFieldKey(fieldKey));
}

/** Imaging lines stay hidden until that imaging type is selected. */
function imagingFollowUpVisible(field, answers) {
  const option = imagingChoice(field);
  if (!option) return null;
  const suffix = (String(field.field_key || '').match(/#\d+$/) || [''])[0];
  const selected = answers[`alliance.index.imaging${suffix}`] ?? (suffix ? undefined : answers['alliance.index.imaging']);
  return optionSelected(selected, option);
}

/** Procedure-row lines such as surgical approach stay hidden until that surgery is selected. */
function procedureFollowUpVisible(field, answers) {
  const when = field.applies_when || '';
  const interbody = /procedure is interbody fusion/i.test(when);
  const olif = /interbody approach is OLIF/i.test(when);
  const decompression = /direct decompression or osteotomy/i.test(when);
  if (!interbody && !olif && !decompression) return null;
  const suffix = (String(field.field_key || '').match(/#\d+$/) || [''])[0];
  if (!suffix) return true;
  const procedure = answers[`alliance.index.procedure${suffix}`];
  if (decompression) return /direct decompression/i.test(String(procedure || ''));
  if (olif) return procedure === 'Interbody fusion' && answers[`alliance.index.approach${suffix}`] === 'OLIF';
  return procedure === 'Interbody fusion';
}

/** Device lines such as Midas irrigation stay hidden until that device answer is Yes. */
function deviceUsedVisible(field, answers) {
  const when = field.applies_when || '';
  if (!/\bwas used\b/i.test(when)) return null;
  const key = String(field.field_key || '');
  const suffix = (key.match(/#\d+$/) || [''])[0];
  const bare = key.slice(0, key.length - suffix.length);
  const device = bare.match(/^(.*\.device\.[^.]+)/);
  if (!device) return null;
  const stem = device[1];
  if (bare === `${stem}.used`) return null;
  const used = answers[`${stem}.used${suffix}`] === 'Yes';
  if (/irrigation/i.test(when)) return used && answers[`${stem}.irrigation${suffix}`] === 'Yes';
  return used;
}

function isOtherChoice(value) {
  if (value === true) return true;
  if (Array.isArray(value)) return value.some((item) => /^other\b/i.test(String(item).trim()));
  return /^other\b/i.test(String(value || '').trim());
}

function otherParentKeys(fieldKey) {
  const match = String(fieldKey || '').match(/^(.*?)(#\d+)?$/);
  const bare = match ? match[1] : '';
  const suffix = match ? (match[2] || '') : '';
  const keys = [];
  if (/(?:_|\.)other_specify$/i.test(bare)) keys.push(bare.replace(/_specify$/i, ''));
  const stripped = bare
    .replace(/_other_specify$/i, '')
    .replace(/_other$/i, '')
    .replace(/\.other_specify$/i, '')
    .replace(/\.other$/i, '');
  if (stripped && stripped !== bare) keys.push(stripped);
  if (/\.other$/i.test(bare)) {
    keys.push(`${stripped}.type`);
    keys.push(`${stripped}.describe`);
  }
  return [...new Set(keys)].map((key) => `${key}${suffix}`);
}

/** Specify lines such as "Other procedure" stay hidden until that row's dropdown is Other. */
function otherDetailVisible(field, answers) {
  const key = String(field.field_key || '');
  const suffix = (key.match(/#\d+$/) || [''])[0];
  const parents = otherParentKeys(key);
  if (!parents.length) return true;
  if (suffix) return parents.some((parent) => isOtherChoice(answers[parent]));
  if (parents.some((parent) => isOtherChoice(answers[parent]))) return true;
  const rowSelected = parents.some((parent) => Object.keys(answers).some((answerKey) => (
    answerKey.startsWith(`${parent}#`) && isOtherChoice(answers[answerKey])
  )));
  if (rowSelected) return true;
  if (field.repeat_group || field.max_repeats) return true;
  return false;
}

function stem(token) {
  if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) return token.slice(0, -1);
  return token;
}

function productTokens(name) {
  return name
    .toLowerCase()
    .replace(/[®™]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .map(stem)
    .filter((token) => token && !GENERIC_TOKENS.has(token) && (token.length > 2 || SHORT_TOKENS.has(token) || /\d/.test(token)));
}

/** Selected device checkbox vs the device named on an eligibility line. */
export function devicesMatch(selectedName, criterionDevice) {
  if (!selectedName || !criterionDevice || criterionDevice === 'All') return false;
  const selected = productTokens(selectedName);
  const criterion = productTokens(criterionDevice);
  if (!selected.length || !criterion.length) return false;
  const criterionSet = new Set(criterion);
  if (!criterionSet.has(selected[0])) return false;
  if (!selected.every((token) => criterionSet.has(token))) return false;
  const selectedSet = new Set(selected);
  const extra = criterion.some((token) => !selectedSet.has(token));
  if (!extra) return true;
  if (selected.length < 2) return false;
  return /&| and /i.test(criterionDevice);
}

export function selectedDeviceNames(deviceFields, values) {
  return deviceFields
    .filter((field) => values[field.field_key] === true && field.device)
    .map((field) => field.device);
}

export function criteriaForDevices(criteria, selectedNames) {
  const groups = new Map();
  criteria.forEach((field) => {
    const matched = field.device === 'All' || selectedNames.some((name) => devicesMatch(name, field.device));
    if (!matched) return;
    const device = field.device || 'All';
    if (!groups.has(device)) groups.set(device, []);
    groups.get(device).push(field);
  });
  return [...groups.entries()]
    .sort((a, b) => {
      if (a[0] === 'All') return -1;
      if (b[0] === 'All') return 1;
      return a[0].localeCompare(b[0]);
    })
    .map(([device, rows]) => ({ device, rows: bundleCriteria(rows) }));
}

function bundleCriteria(rows) {
  const bundles = [];
  const index = new Map();
  rows.forEach((row) => {
    const key = `${row.criterion_type}|${row.criterion_id}|${row.device}`;
    if (!index.has(key)) {
      const bundle = { key, rows: [] };
      index.set(key, bundle);
      bundles.push(bundle);
    }
    index.get(key).rows.push(row);
  });
  return bundles;
}

export function isIntroCriterion(label) {
  return /following|such as|select one|one of the following/i.test(label) || /:\s*$/.test(label);
}

export function splitOptions(value) {
  if (!value) return [];
  return value.split(';').map((part) => part.trim()).filter(Boolean);
}

export function isRepeatHeader(field) {
  return Boolean(field.repeat_group) && /_count$|_not_used$/.test(field.field_key);
}

export function isRepeatItem(field) {
  if (!field.repeat_group || isRepeatHeader(field)) return false;
  if (/_\d+_/.test(field.field_key) || /\.\d+\./.test(field.field_key)) return false;
  const max = field.max_repeats;
  return max === 'repeating' || Number(max) > 1;
}

export function buildBlocks(fields) {
  const repeats = new Map();
  const blocks = [];
  let current = null;

  const flush = () => {
    if (current && current.fields.length) blocks.push(current);
    current = null;
  };

  fields.forEach((field) => {
    if (isRepeatHeader(field) || isRepeatItem(field)) {
      flush();
      if (!repeats.has(field.repeat_group)) {
        repeats.set(field.repeat_group, { id: field.repeat_group, headers: [], items: [] });
        blocks.push({ type: 'repeat', id: field.repeat_group });
      }
      const group = repeats.get(field.repeat_group);
      if (isRepeatHeader(field)) group.headers.push(field);
      else group.items.push(field);
      return;
    }
    const section = field.section || 'Details';
    if (!current || current.section !== section) {
      flush();
      current = { type: 'section', section, fields: [] };
    }
    current.fields.push(field);
  });
  flush();
  return { blocks, repeats };
}

export function repeatRowCount(group, values, repeatState) {
  const notUsed = group.headers.find((field) => field.field_key.endsWith('_not_used'));
  if (notUsed && values[notUsed.field_key] === true) return 0;
  const countField = group.headers.find((field) => field.field_key.endsWith('_count'));
  const maxRaw = group.items[0]?.max_repeats;
  const max = maxRaw === 'repeating' ? 30 : Number(maxRaw) || 10;
  if (countField) {
    const count = parseInt(values[countField.field_key], 10);
    if (!Number.isFinite(count) || count <= 0) return 0;
    return Math.min(count, max);
  }
  const current = repeatState[group.id] || 1;
  return Math.min(Math.max(current, 1), max);
}

export function isFilledValue(value) {
  if (value === true) return true;
  if (Array.isArray(value)) return value.length > 0;
  return value !== '' && value != null && value !== false;
}

function bareAnswerKey(fieldKey) {
  return String(fieldKey || '').replace(/#\d+$/, '');
}

export function calendarDate(value) {
  const match = String(value || '').match(/^(\d{4}-\d{2}-\d{2})(?:T\d{2}:\d{2}(?::\d{2})?)?$/);
  return match ? match[1] : '';
}

function isCalendarField(field) {
  return field?.data_type === 'date' || field?.data_type === 'datetime';
}

/** Date answers on a form, including each repeating row that is already on screen. */
export function dateAnswerKeys(fields, values = {}, repeats = {}) {
  const keys = [];
  (fields || []).forEach((field) => {
    if (!isCalendarField(field) || !field.field_key) return;
    const repeating = isRepeatItem(field);
    if (!repeating) {
      keys.push(field.field_key);
      return;
    }
    const indexed = Object.keys(values || {}).map((key) => {
      const match = key.match(new RegExp(`^${field.field_key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}#(\\d+)$`));
      return match ? Number(match[1]) : null;
    }).filter((index) => index != null);
    const count = Math.max(Number(repeats?.[field.repeat_group]) || 0, indexed.length ? Math.max(...indexed) + 1 : 0, 1);
    for (let index = 0; index < count; index += 1) keys.push(`${field.field_key}#${index}`);
  });
  return keys;
}

export function isDateAnswer(fieldKey, fields) {
  const bare = bareAnswerKey(fieldKey);
  return (fields || []).some((field) => field.field_key === bare && isCalendarField(field));
}

function anotherCalendarAnswer(fieldKey, packets) {
  return (packets || []).some((packet) => dateAnswerKeys(packet.fields, packet.values, packet.repeats).some((key) => (
    key !== fieldKey && packet.values?.[key] && packet.values[key] !== 'N/A'
  )));
}

/** True when this is the first real date entered on the form. */
export function isFirstFormDate(fieldKey, value, fields, values = {}, repeats = {}) {
  return isFirstCalendarAnswer(fieldKey, value, [{ fields, values, repeats }]);
}

/** True when this is the first real date or datetime across the given forms. */
export function isFirstCalendarAnswer(fieldKey, value, packets = []) {
  if (!calendarDate(value)) return false;
  if (!(packets || []).some((packet) => isDateAnswer(fieldKey, packet.fields))) return false;
  return !anotherCalendarAnswer(fieldKey, packets);
}

/** Copies one date onto the other date and datetime fields. Document dates stay calendar days. */
export function applySharedFormDate(fields, values = {}, repeats = {}, date) {
  const day = calendarDate(date);
  const next = { ...(values || {}) };
  if (!day) return next;
  const time = (String(date).match(/T(\d{2}:\d{2})/) || [])[1] || '00:00';
  dateAnswerKeys(fields, next, repeats).forEach((key) => {
    if (next[key] === 'N/A') return;
    const bare = bareAnswerKey(key);
    const field = (fields || []).find((item) => item.field_key === bare);
    const dayOnly = field?.data_type !== 'datetime' || /document_date$/.test(bare);
    next[key] = dayOnly ? day : `${day}T${time}`;
  });
  return next;
}

export function filledCount(values) {
  if (!values) return 0;
  return Object.values(values).filter(isFilledValue).length;
}

function progressResult(filled, total) {
  return {
    filled,
    total,
    percent: total ? Math.round((filled / total) * 100) : 0,
  };
}

/** One checklist, such as every Stryker product used, is a single question. */
export function isChoiceListOption(field) {
  return field?.data_type === 'boolean' && String(field.field_key || '').startsWith('device.');
}

/** A free-text or date line that only exists to name an Other answer. */
function isOtherSpecify(field) {
  const type = field?.data_type;
  if (type !== 'text' && type !== 'date' && type !== 'month' && type !== 'datetime') return false;
  return /other/i.test(field.applies_when || '');
}

function selectedOther(value) {
  if (value === true) return true;
  if (Array.isArray(value)) return value.some((item) => /^other\b/i.test(String(item).trim()));
  return /^other\b/i.test(String(value || '').trim());
}

function tallyQuestions(fields, valueFor, tally) {
  let index = 0;
  while (index < fields.length) {
    if (isDerivedBmi(fields[index])) {
      index += 1;
      continue;
    }
    const unit = fields[index + 1];
    if (
      unit
      && isMeasureUnit(unit)
      && measureKeyForUnit(unit.field_key) === fields[index].field_key
      && splitOptions(unit.response_options).length
    ) {
      tally(isFilledValue(valueFor(fields[index])) && isFilledValue(valueFor(unit)));
      index += 2;
      continue;
    }
    if (isGraftLocationField(fields[index])) {
      const options = [];
      while (index < fields.length && isGraftLocationField(fields[index])) {
        options.push(fields[index]);
        index += 1;
      }
      let specify = null;
      if (index < fields.length && isGraftSpecifyField(fields[index])) {
        specify = fields[index];
        index += 1;
      }
      tally(!graftLocationMissing({ options, specify }, valueFor));
      continue;
    }
    if (isChoiceListOption(fields[index])) {
      let answered = false;
      while (index < fields.length && isChoiceListOption(fields[index])) {
        if (valueFor(fields[index]) === true) answered = true;
        index += 1;
      }
      tally(answered);
      continue;
    }
    const parent = fields[index];
    index += 1;
    const details = [];
    while (index < fields.length && isOtherSpecify(fields[index])) {
      details.push(fields[index]);
      index += 1;
    }
    const main = valueFor(parent);
    const answered = isFilledValue(main) && (
      !selectedOther(main) || details.every((field) => isFilledValue(valueFor(field)))
    );
    tally(answered);
  }
}

function tallyBlock(block, repeats, values, repeatState, tally) {
  if (block.type === 'repeat') {
    const group = repeats.get(block.id);
    if (!group) return;
    const headers = group.headers.filter((field) => isFieldVisible(field, values));
    const items = group.items.filter((field) => isFieldVisible(field, values));
    if (!headers.length && !items.length) return;
    tallyQuestions(headers, (field) => values[field.field_key], tally);
    if (!items.length) return;
    const rows = repeatRowCount(group, values, repeatState);
    for (let index = 0; index < rows; index += 1) {
      const rowItems = items.filter((field) => isFieldVisible(
        { ...field, field_key: `${field.field_key}#${index}` },
        values,
      ));
      tallyQuestions(rowItems, (field) => values[`${field.field_key}#${index}`], tally);
    }
    return;
  }
  tallyQuestions(
    block.fields.filter((field) => isFieldVisible(field, values)),
    (field) => values[field.field_key],
    tally,
  );
}

function blockProgress(block, repeats, values, repeatState) {
  let total = 0;
  let filled = 0;
  tallyBlock(block, repeats, values, repeatState, (answered) => {
    total += 1;
    if (answered) filled += 1;
  });
  return progressResult(filled, total);
}

function subsectionId(block, seen) {
  if (block.type === 'repeat') return `repeat:${block.id}`;
  const name = block.section || 'Details';
  const count = seen.get(name) || 0;
  seen.set(name, count + 1);
  return count ? `section:${name}#${count}` : `section:${name}`;
}

/** Share of the questions currently shown on a form that have an answer. */
export function formProgress(fields, values = {}, repeatState = {}) {
  const list = (fields || []).filter((field) => field.field_key && field.field_key !== 'criteria-placeholder');
  const { blocks, repeats } = buildBlocks(list);
  let total = 0;
  let filled = 0;
  blocks.forEach((block) => {
    const progress = blockProgress(block, repeats, values, repeatState);
    total += progress.total;
    filled += progress.filled;
  });
  return progressResult(filled, total);
}

/** Sections and repeat groups whose visible questions are all answered. */
export function completedSectionIds(fields, values = {}, repeatState = {}) {
  const list = (fields || []).filter((field) => field.field_key && field.field_key !== 'criteria-placeholder');
  const { blocks, repeats } = buildBlocks(list);
  const seen = new Map();
  const ids = [];
  blocks.forEach((block) => {
    const id = subsectionId(block, seen);
    const progress = blockProgress(block, repeats, values, repeatState);
    if (progress.total > 0 && progress.filled === progress.total) ids.push(id);
  });
  return ids;
}

/** Form-section ids that are complete on the current chart. */
export function completedSubsectionKeys(outline, forms) {
  const keys = [];
  outlineStops(outline).forEach((stop) => {
    const formKey = instanceKey(stop.form.id, stop.visitId);
    const saved = forms?.[formKey] || {};
    completedSectionIds(stop.form.fields, saved.values || {}, saved.repeats || {}).forEach((id) => {
      keys.push(`${formKey}::${id}`);
    });
  });
  return keys;
}

/** Share of the criteria lines on screen that are marked Met or Not met. */
export function criteriaProgress(criteria, deviceFields, deviceValues, values = {}, reviewerFields = []) {
  const groups = criteriaForDevices(criteria, selectedDeviceNames(deviceFields, deviceValues));
  let total = 0;
  let filled = 0;
  groups.forEach((group) => {
    group.rows.forEach((bundle) => {
      const [first, ...rest] = bundle.rows;
      const intro = bundle.rows.length > 1 && isIntroCriterion(first.field_label);
      (intro ? rest : bundle.rows).forEach((field) => {
        total += 1;
        if (isFilledValue(values[field.field_key])) filled += 1;
      });
    });
  });
  reviewerFields.filter((field) => isFieldVisible(field, values)).forEach((field) => {
    total += 1;
    if (isFilledValue(values[field.field_key])) filled += 1;
  });
  return progressResult(filled, total);
}

/** Criteria and reviewer questions in the order they appear, for What's next. */
export function criteriaQuestionEntries(criteria, deviceFields, deviceValues, values = {}, reviewerFields = []) {
  const groups = criteriaForDevices(criteria, selectedDeviceNames(deviceFields, deviceValues));
  const entries = [];
  groups.forEach((group) => {
    group.rows.forEach((bundle) => {
      const [first, ...rest] = bundle.rows;
      const intro = bundle.rows.length > 1 && isIntroCriterion(first.field_label);
      (intro ? rest : bundle.rows).forEach((field) => {
        entries.push({
          fieldKey: field.field_key,
          missing: !isFilledValue(values[field.field_key]),
          kind: 'criterion',
        });
      });
    });
  });
  (reviewerFields || []).filter((field) => isFieldVisible(field, values)).forEach((field) => {
    entries.push({
      fieldKey: field.field_key,
      missing: !isFilledValue(values[field.field_key]),
      kind: 'review',
    });
  });
  return entries;
}
